// steps/SelectModel.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const SelectModel = ({
  handleNext,
  handleBackButton,
  selectedCountry,
  selectedOption,
  selectedUserId,
  onModelSelected, // Callback to pass selected model data back to parent
}) => {
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [modelName, setModelName] = useState('');
  const [modelDesc, setModelDesc] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDisaggregated = selectedOption === 'Disaggregated';

  const fetchModels = () => {
    let url;
    if (isDisaggregated) {
      url = `https://flexibility-backend.dedalus.epu.ntua.gr/models/disaggregation?user_id=${selectedUserId}&country=${selectedCountry}`;
    } else {
      url = `https://flexibility-backend.dedalus.epu.ntua.gr/models/get/?user_id=${selectedUserId}&country=${selectedCountry}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Fetched models:', data);
        setModels(data);
      })
      .catch((error) => console.error('Error fetching models:', error));
  };

  useEffect(() => {
    fetchModels();
  }, [selectedCountry, selectedOption, selectedUserId]);

  const handleModelDialogOpen = () => {
    setModelDialogOpen(true);
  };

  const handleModelDialogClose = () => {
    setModelDialogOpen(false);
    setIsCreatingNew(false); // Reset creation state on close
  };

  const handleSubmitModel = () => {
    if (isCreatingNew) {
      // Logic for creating a new model
      setSelectedModel(modelName);
      onModelSelected({
        modelName,
        modelDesc,
        isNewModel: true,
      });
    } else {
      // Logic for selecting an existing model
      const selectedModelObj = models.find(model => model.model_name === selectedModel);
      setModelName(selectedModel);
      onModelSelected({
        modelName: selectedModel,
        modelDesc: selectedModelObj?.model_desc || '',
        isNewModel: false,
      });
    }
    handleModelDialogClose();
  };

  const handleModelChange = (event) => {
    const selectedModelName = event.target.value;
    setSelectedModel(selectedModelName);
    setModelName(selectedModelName);

    // Find the selected model object
    const selectedModelObj = models.find(model => model.model_name === selectedModelName);

    // Pass model data back to parent
    onModelSelected({
      modelName: selectedModelName,
      modelDesc: selectedModelObj?.model_desc || '',
      isNewModel: false
    });

    handleModelDialogClose();
  };

  const handleDeleteAllModels = () => {
    setIsDeleting(true);

    fetch(`https://flexibility-backend.dedalus.epu.ntua.gr/models/delete-all/${selectedUserId}/${selectedCountry}`, {
      method: 'DELETE',
      headers: { accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((msg) => { throw new Error(msg); });
        }
        return response.json();
      })
      .then(() => {
        setModels([]);
        setSelectedModel('');
        setModelName('');
        onModelSelected({ modelName: '', modelDesc: '', isNewModel: true });
        setDeleteDialogOpen(false);
      })
      .catch((error) => {
        alert(`Failed to delete models: ${error.message}`);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <Box>
      <Typography>Choose an option:</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 3 }}>
        <Button onClick={handleModelDialogOpen} variant="contained">
          Select or Create Model
        </Button>
        {models.length > 0 && (
          <Button
            onClick={() => setDeleteDialogOpen(true)}
            variant="text"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
          >
            Delete All Models
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography>Selected Model: {selectedModel}</Typography>
      </Box>

      {/* Unified Model Dialog */}
      <Dialog open={modelDialogOpen} onClose={handleModelDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{isCreatingNew ? 'Create New Model' : 'Select a Model'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCreatingNew}
                onChange={(e) => setIsCreatingNew(e.target.checked)}
                name="createNew"
                color="primary"
              />
            }
            label="Create a new model"
          />

          {isCreatingNew ? (
            <>
              <TextField
                label="Model Name"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Model Description"
                value={modelDesc}
                onChange={(e) => setModelDesc(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          ) : (
            <FormControl fullWidth sx={{ padding: '5px', mt: 2 }}>
              <Select
                labelId="model-select-label"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                displayEmpty
              >
                {Array.isArray(models) && models.length > 0 ? (
                  models.map((model) => (
                    <MenuItem key={model.model_name} value={model.model_name}>
                      {model.model_name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No models found</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModelDialogClose}>Cancel</Button>
          <Button
            onClick={handleSubmitModel}
            disabled={isCreatingNew ? !modelName : !selectedModel}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete All Models</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all your models for {selectedCountry}. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDeleteAllModels} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2 }}>
        <Button onClick={handleBackButton} variant="text">
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!selectedModel}
          variant='outlined'
          color="primary"
          sx={{ ml: 2 }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default SelectModel;
